import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/ui/form';
import { useInternalScheduleForm } from '@forms/schedules/internal';
import { defaultValues } from '@forms/schedules/internal/constants';
import { useRemoveInternalSchedule } from '@services/schedules/internal/remove';
import { toTenantDate } from '@utils/timeZone';
import { getFullName } from '@utils/textHelpers';
import { RelatedCardView } from '../view';
import { RelatedConfirmed } from '../confirmed';
import { useSettle } from '../../useSettle';
import { shiftIso } from '../../helpers';
import { RelatedFormCardProps } from '../types';
import { RelatedInternalQuickEdit } from './quickEdit';

export const RelatedInternalCard = ({
  data,
  shiftDays,
  updated,
  register,
  icon,
  ...view
}: RelatedFormCardProps) => {
  const { t } = useTranslation('app');
  const { id, startsAt, endsAt, status, foreman } = data;
  const taskId = data.task?.id || '';
  const jobId = data.task?.job?.id || '';

  const { form, onSubmit, locked } = useInternalScheduleForm({
    schedule: data,
    successHandler: () => save.finish({ ok: true }),
    onOverlap: () => {
      form.setValue('forceOverlapping', true);
      save.finish({ ok: false, error: 'overlap' });
    },
  });
  const save = useSettle(locked);

  const removal = useRemoveInternalSchedule({
    id,
    jobId,
    taskId,
    callback: (resp) => remove.finish({ ok: Boolean(resp?.success), error: 'removeFailed' }),
  });
  const remove = useSettle(removal.isLoading);

  useEffect(() => {
    const days = updated ? 0 : shiftDays;
    form.reset({
      ...defaultValues,
      startsAt: shiftIso(startsAt, days),
      endsAt: shiftIso(endsAt, days),
      foremanId: foreman?.id || '',
      relatedScheduleForemanId: foreman?.id || '',
      confirmed: status === 'approved',
    });
  }, [startsAt, endsAt, status, foreman?.id, shiftDays, updated]);

  useEffect(() => {
    register(id, {
      move: () =>
        save.run(
          async () => {
            let invalid = false;
            await form.handleSubmit(onSubmit, () => {
              invalid = true;
            })();
            return !invalid && !Object.keys(form.formState.errors).length;
          },
          { ok: false, error: 'saveFailed' },
        ),
      remove: () =>
        remove.run(
          async () => {
            removal.mutate();
            return true;
          },
          { ok: false, error: 'removeFailed' },
        ),
    });
    return () => register(id);
  });

  const nextStart = form.watch('startsAt');
  const foremanName = foreman?.id
    ? foreman.fullName || getFullName(foreman.firstName, foreman.lastName)
    : t('scheduling.phone.unassigned');

  return (
    <Form {...form}>
      <RelatedCardView
        {...view}
        name={data.task?.name || ''}
        icon={icon}
        owner={foremanName}
        from={toTenantDate(startsAt)}
        to={nextStart ? toTenantDate(nextStart) : undefined}
        updated={updated}
        confirmed={<RelatedConfirmed id={id} disabled={view.disabled} />}>
        <RelatedInternalQuickEdit data={data} disabled={view.disabled} />
      </RelatedCardView>
    </Form>
  );
};
